/**
 * Fidora AI Chat Service - Serverless Backend
 * Handles all AI chat requests via Firebase Cloud Functions
 */

import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getApp } from 'firebase/app';

// Prefer explicit region to avoid cross-region callable failures
const FUNCTIONS_REGION = process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || 'us-central1';
const USE_EMULATOR = String(process.env.REACT_APP_USE_FUNCTIONS_EMULATOR || '').toLowerCase() === 'true';

let cachedFunctions = null;
function getFunctionsInstance() {
  if (cachedFunctions) return cachedFunctions;
  try {
    cachedFunctions = getFunctions(getApp(), FUNCTIONS_REGION);
  } catch (e) {
    // Fallback to default app if getApp() not yet accessible in rare cases
    cachedFunctions = getFunctions(undefined, FUNCTIONS_REGION);
  }
  if (USE_EMULATOR) {
    try {
      connectFunctionsEmulator(cachedFunctions, 'localhost', 5001);
    } catch (_) {
      // ignore if emulator already connected
    }
  }
  return cachedFunctions;
}

/**
 * Send a message to the AI agent via Firebase Cloud Functions
 * Uses ONLY Firebase - no local backend fallback
 * Connects to 65-block coaching system with 1,321 Firebase prompts
 * @param {string} message - The user's message
 * @param {Array} history - The conversation history
 * @param {string} userName - Optional user name for personalization
 * @returns {Promise<{reply: string, mcpAgents: Array}>} The AI's reply and MCP agents used
 */
export async function sendMessage(message, history = [], userName = null) {
  try {
    console.log('🚀 Sending message to Firebase Cloud Function (65-Block Coaching + MCP Agents)...');
    
    const functions = getFunctionsInstance();
    const chatFunction = httpsCallable(functions, 'fidoraChat');
    
    const result = await chatFunction({
      message,
      history,
      userName
    });
    
    console.log('✅ Got personalized response from Firebase with', result.data.mcpAgentsUsed?.length || 0, 'MCP agents');
    
    // Return just the reply string for backward compatibility
    return result.data.reply || result.data;
  } catch (error) {
    console.error('❌ Error with Firebase callable function:', error);

    // Provide more helpful error messages
    if (error?.code === 'functions/unauthenticated') {
      throw new Error('Please sign in to chat with Fidora.');
    } else if (error?.code === 'functions/permission-denied') {
      throw new Error('You don\'t have permission to use this feature.');
    } else if (error?.code === 'functions/failed-precondition') {
      // This means a coaching prompt is missing in Firestore
      throw new Error(error?.message || 'Coach configuration error. Please contact support.');
    }

    // Surface the original error - no fallbacks
    const msg = error?.message || 'Failed to get response from AI. Please check your internet connection and try again.';
    throw new Error(msg);
  }
}

/**
 * Transcribe audio using Firebase Cloud Functions
 * @param {Blob} audioBlob - The audio data
 * @returns {Promise<string>} The transcribed text
 */
export async function transcribeAudio(audioBlob) {
  try {
    const functions = getFunctionsInstance();
    const transcribeFunction = httpsCallable(functions, 'fidoraTranscribe');
    
    // Convert blob to base64
    const reader = new FileReader();
    const base64Audio = await new Promise((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(audioBlob);
    });
    
    const result = await transcribeFunction({
      audio: base64Audio,
      format: audioBlob.type
    });
    
    return result.data.transcript;
  } catch (error) {
    console.error('❌ Error transcribing audio:', error);
    throw new Error('Failed to transcribe audio. Please try again.');
  }
}

/**
 * Generate speech from text using Firebase Cloud Functions
 * @param {string} text - The text to convert to speech
 * @param {string} voice - Voice ID (optional)
 * @returns {Promise<Blob>} Audio blob
 */
export async function generateSpeech(text, voice = 'nova') {
  try {
    const functions = getFunctionsInstance();
    const ttsFunction = httpsCallable(functions, 'fidoraTTS');
    
    const result = await ttsFunction({
      text,
      voice
    });
    
    // Convert base64 back to blob
    const base64Audio = result.data.audio;
    const binaryString = window.atob(base64Audio);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    return new Blob([bytes], { type: 'audio/mpeg' });
  } catch (error) {
    console.error('❌ Error generating speech:', error);
    throw new Error('Failed to generate speech. Please try again.');
  }
}

